import Grid from '@mui/material/Grid';

import { PHONENUMBER_FORMAT } from '../../constants';
import { contactApiKeyStore } from '../../api/contact-api-key-store';
import { FormInput } from '../../components/hook-form/form-input.component';
import { FormCheckbox } from '../../components/hook-form/form-checkbox.component';
import { FormFormattedInput } from '../../components/hook-form/form-formatted-input.component';
import { FormSelect } from '../../components/hook-form/form-select.component';
import { FormAutoCompleteSelectDynamic } from '../../components/hook-form/form-autocomplete-select.dynamic.component';
import { stateApiKeyStore } from '../../api/states-api-key-store';

import { Tooltip, Typography } from '@mui/material';
import { Icons } from '../../assets/icons';
import { useParams } from 'react-router-dom';
import { UserPermissionName } from '../../models/user-permission-name';
import { useAuthorize } from '../../hooks/use-authorize';
import { useFormContext } from 'react-hook-form';
import { FormAutoCompleteMultiSelectDynamic } from '../query-builder/hooks-form-querybuilder/form-autocomplete-multi-select.dynamic.component';
import { useSnackbar } from '../../hooks/use-snackbar.hook';

const CONTACT_TITLE = [
  {
    value: 'Mr',
    label: 'Mr',
  },
  {
    value: 'Mrs',
    label: 'Mrs',
  },
  {
    value: 'Ms',
    label: 'Ms',
  },
];

interface ContactForm {
  page?: string;
  isSyncWithDux?: boolean;
  handleDuxAction: (value: any) => void;
}

export const ContactForm = ({ page, isSyncWithDux, handleDuxAction }: ContactForm) => {
  const { showSnackbar } = useSnackbar();
  const { getValues, setValue } = useFormContext();
  const isDUXEnabled = process.env.REACT_APP_DUX_FLAG === 'true';
  const { id, officeID, projectID, unitID } = useParams();

  const handlePPIReportsChange = (selectedValues: any[]) => {
    const hasAllReports = selectedValues.some(
      item => item.notificationReportStatus === "All Reports"
    );

    const updatedData = hasAllReports
      ? selectedValues.filter(
        item =>
          item.notificationReportStatus === "All Reports" ||
          item.notificationReportStatus === "Not Ready"
      )
      : selectedValues;

    const hasInvalidOther = selectedValues.some(
      item =>
        item.notificationReportStatus !== "All Reports" &&
        item.notificationReportStatus !== "Not Ready"
    );

    if (hasAllReports && hasInvalidOther)
      showSnackbar(`All Reports cannot be selected with other report statuses except Not Ready.`, 'warning');

    setValue("ppiReportStatusIDs", updatedData);
  };

  const handleSUSReportsChange = (selectedValues: any[]) => {
  const hasAllReports = selectedValues.some(
      item => item.notificationReportStatus === "All Reports"
    );

    const updatedData = hasAllReports
      ? selectedValues.filter(
        item =>
          item.notificationReportStatus === "All Reports" ||
          item.notificationReportStatus === "Not Ready"
      )
      : selectedValues;

    const hasInvalidOther = selectedValues.some(
      item =>
        item.notificationReportStatus !== "All Reports" &&
        item.notificationReportStatus !== "Not Ready"
    );

    if (hasAllReports && hasInvalidOther)
      showSnackbar(`All Reports cannot be selected with other report statuses except Not Ready.`, 'warning');

    setValue("susReportStatusIDs", updatedData);
  }

  const handleOtherReportsChange = (selectedValues: any[]) => {const hasAllReports = selectedValues.some(
      item => item.notificationReportStatus === "All Reports"
    );

    const updatedData = hasAllReports
      ? selectedValues.filter(
        item =>
          item.notificationReportStatus === "All Reports" ||
          item.notificationReportStatus === "Not Ready"
      )
      : selectedValues;

    const hasInvalidOther = selectedValues.some(
      item =>
        item.notificationReportStatus !== "All Reports" &&
        item.notificationReportStatus !== "Not Ready"
    );

    if (hasAllReports && hasInvalidOther)
      showSnackbar(`All Reports cannot be selected with other report statuses except Not Ready.`, 'warning');

    setValue("otherReportStatusIDs", updatedData);
  }

  const isSyncWithDuxPermission = useAuthorize([UserPermissionName?.SYNC_WITH_DUX]);

  const handleDuxCheckbox = (event: any, checked: any) => { handleDuxAction(checked) }

  const srcpage = page == 'municipality' ? 'municipality' : null

  return (
    <Grid container spacing={2}>
      <Grid item lg={6.7} sm={6.7} xl={6.7} xs={6.7}>
        <Grid container spacing={2}>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <Grid container spacing={2}>
              <Grid item lg={3} sm={3} xl={3} xs={3}>
                <FormSelect name="contactsTitle" label=" Title" options={CONTACT_TITLE} />
              </Grid>
              <Grid item lg={9} sm={9} xl={9} xs={9}>
                <FormInput name="contactsFirstName" label="First Name" />
              </Grid>
            </Grid>
          </Grid>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <FormInput name="contactsLastName" label="Last Name" />
          </Grid>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <FormInput
              name="contactsWorkEmail"
              label="Work Email"
              validationQueryRoute={(value: any) => value && contactApiKeyStore.validateContactEmail(getValues('contactsID') ?? 0, value ?? '')}
            />
          </Grid>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <FormInput
              name="contactsSecondaryEmail"
              label="Secondary Email"
              validationQueryRoute={(value: any) => value && contactApiKeyStore.validateContactEmail(getValues('contactsID') ?? 0, value)}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <FormFormattedInput name="contactsOfficePhone" label="Office Phone" format={PHONENUMBER_FORMAT} />
          </Grid>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <FormInput name="contactsOfficePhoneExt" label="Extension" />
          </Grid>
          <Grid item lg={6} sm={6} xl={6} xs={6} style={{ display: 'flex' }}>
            <FormFormattedInput
              name="contactsCellPhone"
              label="Cell Phone"
              format={PHONENUMBER_FORMAT}
              validationQueryRoute={(value: any) =>
                value && contactApiKeyStore.validateContactPhone(getValues('contactsID') ?? 0, value ?? '')
              }
            />
            {(page === 'project' || page === 'office' || page === 'customer') && isDUXEnabled && (
              <Tooltip
                style={{ marginLeft: '5px' }}
                title={<Typography variant="body1">Cell Phone required for DUX inspections</Typography>}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <img src={Icons.IconWarning} style={{ maxHeight: '20px', maxWidth: '20px', marginLeft: '5px' }} />
                </div>
              </Tooltip>
            )}
          </Grid>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <FormFormattedInput name="contactsOtherPhone" label="Other Phone" format={PHONENUMBER_FORMAT} />
          </Grid>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <FormInput name="contactsOtherPhoneExt" label="Other Phone Extension" />
          </Grid>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <FormInput name="specialInstructions" label="Special Instruction" />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <FormAutoCompleteSelectDynamic
              name="contactRole"
              label="Contact Role"
              getQueryKeyConfig={
                page === 'municipality'
                  ? () => contactApiKeyStore.getContactMuncipalityRoles()
                  : () => contactApiKeyStore.getContactRoles()
              }
              optionsValueKey="contactsRoleID"
              optionsLabelKey="contactsRoleName"
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item lg={5.3} sm={5.3} xl={5.3} xs={5.3}>
        <Grid container spacing={1.3}>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <FormInput name="contactsDescription" label="Description" multiline size={3.4} />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <FormAutoCompleteMultiSelectDynamic
              name="contactsNotificationIDs"
              label="Notifications"
              getQueryKeyConfig={() => contactApiKeyStore.getNotificationReportsStatus("RPT_NOTF", srcpage)}
              optionsValueKey="notificationReportStatusID"
              optionsLabelKey="notificationReportStatus"
            />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Typography variant="body1" color="textPrimary">
              <b>Send Inspection Report</b>
            </Typography>
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <FormAutoCompleteMultiSelectDynamic
              name="ppiReportStatusIDs"
              label="PPI Reports"
              onChange={handlePPIReportsChange}
              getQueryKeyConfig={() => contactApiKeyStore.getNotificationReportsStatus("RPT_PPI", srcpage)}
              optionsValueKey="notificationReportStatusID"
              optionsLabelKey="notificationReportStatus"
            />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <FormAutoCompleteMultiSelectDynamic
              name="susReportStatusIDs"
              label="SUS Reports"
              onChange={handleSUSReportsChange}
              getQueryKeyConfig={() => contactApiKeyStore.getNotificationReportsStatus("RPT_SUS", srcpage)}
              optionsValueKey="notificationReportStatusID"
              optionsLabelKey="notificationReportStatus"
            />
          </Grid>
          {page !== 'municipality' && (
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <FormAutoCompleteMultiSelectDynamic
                name="otherReportStatusIDs"
                label="Other Reports"
                onChange={handleOtherReportsChange}
                getQueryKeyConfig={() => contactApiKeyStore.getNotificationReportsStatus("RPT_OTH", srcpage)}
                optionsValueKey="notificationReportStatusID"
                optionsLabelKey="notificationReportStatus"
              />
            </Grid>
          )}
          <Grid item lg={3} sm={3} xl={3} xs={3}>
            <FormCheckbox label="Active" name="isActive" />
          </Grid>
          <Grid item lg={5} sm={5} xl={5} xs={5}>
            <FormCheckbox label="Fast Field" name="sendReport" />
          </Grid>
          <Grid item lg={4} sm={4} xl={4} xs={4}>
            <FormCheckbox label="Send Report" name="isSendReportEmail" />
          </Grid>
          {page !== 'municipality' && page !== 'unit' && page !== 'workOrderList' && isDUXEnabled && isSyncWithDuxPermission && (
            <Grid item lg={4} sm={4} xl={4} xs={4}>
              {/* <FormCheckbox label="Sync With DUX" name="syncWithDux" disabled={!id && !officeID && !projectID} /> */}
              <FormCheckbox disabled={!isSyncWithDux} label="Sync With DUX" name="syncWithDux" onChange={handleDuxCheckbox} />
            </Grid>
          )}
          {page !== 'municipality' && (
            <Grid item lg={4} sm={4} xl={4} xs={4}>
              <FormCheckbox label="Master Contact" name="isMasterContacts" />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
